import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, User, X, FileText, Image as ImageIcon, File } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { showToast } from '../../utils/toast';
import { formatRelativeTimeEST } from '../../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientMessagingTab({ clientId, clientInfo }) {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [clientId]);

  useEffect(() => {
    // Only auto-scroll on initial load
    if (isInitialLoad.current && messages.length > 0) {
      scrollToBottom();
      isInitialLoad.current = false;
    }
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API}/api/admin/messages/${clientId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        
        // Mark messages as read
        if (data.unread_count > 0) {
          await fetch(`${API}/api/admin/messages/mark-read/${clientId}`, {
            method: 'PATCH',
            credentials: 'include'
          });
        }
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      showToast({
        title: 'File too large',
        message: 'Maximum file size is 10MB',
        type: 'error'
      });
      return;
    }

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      showToast({
        title: 'Invalid file type',
        message: 'Only PDF, JPG, PNG, and DOCX files are allowed',
        type: 'error'
      });
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/api/client/messages/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setAttachment({
          url: data.attachment_url,
          filename: data.attachment_filename,
          type: data.attachment_type
        });
        showToast({
          title: 'File uploaded',
          message: 'File attached to message',
          type: 'success'
        });
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showToast({
        title: 'Upload failed',
        message: 'Could not upload file',
        type: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim() && !attachment) {
      return;
    }

    setSending(true);

    try {
      const response = await fetch(`${API}/api/admin/messages/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          client_id: clientId,
          message_text: messageText.trim(),
          attachment_url: attachment?.url,
          attachment_filename: attachment?.filename,
          attachment_type: attachment?.type
        })
      });

      if (response.ok) {
        setMessageText('');
        setAttachment(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        await fetchMessages();
        // Scroll to bottom after sending a message
        setTimeout(() => scrollToBottom(), 100);
        showToast({
          title: 'Message sent',
          message: 'Your message has been delivered',
          type: 'success'
        });
      } else {
        throw new Error('Send failed');
      }
    } catch (error) {
      console.error('Send error:', error);
      showToast({
        title: 'Send failed',
        message: 'Could not send message',
        type: 'error'
      });
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    return formatRelativeTimeEST(timestamp);
  };

  const getAttachmentIcon = (type) => {
    switch (type) {
      case 'image': return <ImageIcon className="h-4 w-4" />;
      case 'pdf': return <FileText className="h-4 w-4" />;
      default: return <File className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-600 text-sm">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[600px] bg-gray-50 rounded-xl border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white p-4 rounded-t-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-md">
            <User className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{clientInfo.business_name}</h3>
            <p className="text-sm text-gray-600">
              {clientInfo.primary_contact_name} • {clientInfo.assigned_staff}
            </p>
          </div>
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 rounded-full bg-gray-100 mx-auto mb-4 flex items-center justify-center">
              <Send className="h-8 w-8 text-gray-400" />
            </div>
            <p className="text-gray-900 font-semibold mb-1">No messages yet</p>
            <p className="text-gray-500 text-sm">Send a message to start the conversation</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.message_id}
              className={`flex ${msg.sender_type === 'staff' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[75%] ${
                  msg.sender_type === 'staff'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                    : 'bg-white border border-gray-200'
                } rounded-2xl p-3 shadow-sm`}
              >
                {msg.sender_type === 'staff' && (
                  <p className="text-xs font-semibold text-blue-100 mb-1">{msg.sender_name}</p>
                )}
                
                <p className={`text-sm whitespace-pre-wrap break-words ${
                  msg.sender_type === 'staff' ? 'text-white' : 'text-gray-900'
                }`}>
                  {msg.message_text}
                </p>
                
                {msg.attachment_url && (
                  <div className={`mt-2 pt-2 border-t ${
                    msg.sender_type === 'staff' ? 'border-blue-500' : 'border-gray-200'
                  }`}>
                    <a
                      href={`${API}${msg.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex items-center gap-2 text-xs ${
                        msg.sender_type === 'staff' 
                          ? 'text-blue-100 hover:text-white' 
                          : 'text-blue-600 hover:text-blue-800'
                      }`}
                    >
                      {getAttachmentIcon(msg.attachment_type)}
                      <span className="truncate">{msg.attachment_filename}</span>
                    </a>
                    
                    {msg.attachment_type === 'image' && (
                      <img
                        src={`${API}${msg.attachment_url}`}
                        alt={msg.attachment_filename}
                        className="mt-2 rounded-lg max-w-full h-auto"
                      />
                    )}
                  </div>
                )}
                
                <p className={`text-xs mt-1.5 ${
                  msg.sender_type === 'staff' ? 'text-blue-100' : 'text-gray-500'
                }`}>
                  {formatTime(msg.timestamp)}
                  {msg.is_read && msg.sender_type === 'staff' && (
                    <span className="ml-2">✓✓</span>
                  )}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-gray-200 bg-white p-4 rounded-b-xl">
        {attachment && (
          <div className="mb-3 bg-gray-100 rounded-lg p-2 flex items-center justify-between">
            <div className="flex items-center gap-2 text-gray-700 text-sm flex-1 min-w-0">
              {getAttachmentIcon(attachment.type)}
              <span className="truncate">{attachment.filename}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setAttachment(null)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        )}
        
        <form onSubmit={handleSendMessage} className="flex gap-2">
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.jpg,.jpeg,.png,.docx"
            className="hidden"
          />
          
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || sending}
            className="text-gray-600 hover:bg-gray-100"
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Paperclip className="h-5 w-5" />
            )}
          </Button>
          
          <Textarea
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendMessage(e);
              }
            }}
            placeholder="Type your message..."
            rows={1}
            className="flex-1 resize-none border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-lg"
          />
          
          <Button
            type="submit"
            disabled={(!messageText.trim() && !attachment) || sending}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {sending ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </form>
        
        <p className="text-xs text-gray-500 mt-2">
          Press Enter to send • Shift+Enter for new line
        </p>
      </div>
    </div>
  );
}
